import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import { Elements } from "@stripe/react-stripe-js";
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { Outlet } from 'react-router-dom';
import AuthenticationManager from "./AuthenticationManager";
import NewVersionNotification from './NewVersionNotification';
import GettingStartedIntroCarousel from './GettingStartedIntroCarousel';
import { fetchNotifications } from "../store/actions/notifications";
import { userCanAccessAllTenants, userHasPermission } from "../swzutils/UserPermissionsUtil";
import 'styles/subtrux-antd.less';
import StripeHelper from "../StripeHelper";
import { useInterval } from "usehooks-ts";
import { useAppDispatch, useAppSelector } from "../store";
import { envNameSelector, loggedInUserSelector } from "../store/slice/auth";
import { boxedLayoutSelector, fixedHeaderSelector } from "../store/slice/settings";
export default function App() {
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const boxedLayout = useAppSelector(boxedLayoutSelector);
    const fixedHeader = useAppSelector(fixedHeaderSelector);
    const envName = useAppSelector(envNameSelector);
    const dispatch = useAppDispatch();
    const [stateNotificationPollingEnabled, setStateNotificationPollingEnabled] = useState(false);
    useInterval(() => dispatchFetchNotifications(), stateNotificationPollingEnabled ? 90_000 : null);
    useEffect(() => {
        if (loggedInUser && !userCanAccessAllTenants(loggedInUser) && userHasPermission(loggedInUser, ['admin:notification:list'])) {
            setStateNotificationPollingEnabled(true);
            dispatchFetchNotifications();
        }
        else {
            setStateNotificationPollingEnabled(false);
        }
    }, [loggedInUser]);
    const dispatchFetchNotifications = () => {
        fetchNotifications()(dispatch);
    };
    const envClassName = " env-" + (envName ?? 'unknown');
    return (_jsx(ConfigProvider, { locale: enUS, theme: {
            "token": {
                "colorPrimary": "#999999",
                "colorInfo": "#43cdeb",
                "colorInfoBorder": "#004F79",
                "colorSuccess": "#52c41a",
                "colorWarning": "#ca9603",
                "colorError": "#df052c",
                "colorLinkHover": "#f03d3e",
                "fontFamily": "'Exo', sans-serif"
            },
            "components": {
                "Button": {
                    "colorPrimary": "#000",
                    "colorPrimaryHover": "#444",
                    "colorLink": "#000",
                    "colorLinkHover": "#888",
                    "colorLinkActive": "#444"
                },
                "Calendar": {
                    "controlItemBgActive": "#f03d3e"
                },
                "Card": {
                    "boxShadow": "none"
                },
                "Checkbox": {
                    "colorPrimary": "#000"
                },
                "Input": {
                    "colorTextPlaceholder": '#777'
                },
                "Layout": {
                    "headerBg": "#000"
                },
                "Menu": {
                    "dropdownWidth": 200,
                    "itemColor": "#fff",
                    "darkItemColor": "#fff",
                    "itemSelectedBg": "#f04d3e",
                    "darkItemSelectedBg": "#f04d3e",
                    "itemBg": "#000",
                    "darkItemBg": "#000",
                    "subMenuItemBg": "#000",
                    "darkSubMenuItemBg": "#000",
                    "itemHoverBg": "#f04d3e",
                    "darkItemHoverBg": "#f04d3e",
                    "groupTitleColor": "#fff",
                    "darkGroupTitleColor": "#fff",
                    "itemSelectedColor": "#fff",
                    "darkItemSelectedColor": "#fff",
                    "horizontalItemSelectedColor": "#f04d3e",
                    "horizontalItemHoverColor": "#f04d3e",
                    "horizontalItemSelectedBg": "#f04d3e"
                },
                "Select": {
                    "colorPrimaryHover": "#000",
                    "colorFillSecondary": "rgba(0, 0, 0, 0.06)",
                    "controlItemBgActive": "#f04d3e",
                    "controlItemBgHover": "rgba(0, 0, 0, 0.04)",
                    "fontSizeSM": 18
                },
                "Steps": {
                    "colorTextLabel": "rgba(0, 0, 0, 0.65)",
                    "colorPrimary": "#000000",
                    "colorTextQuaternary": "rgba(0, 0, 0, 0.25)",
                    "colorFillContent": "rgba(0, 0, 0, 0.06)",
                    "controlItemBgActive": "#fff",
                    "colorBorderBg": "#ffffff",
                    "colorBorderSecondary": "#f0f0f0",
                    "colorBgContainer": "#00ff00",
                    "colorTextDisabled": "rgba(0, 0, 0, 0.25)"
                },
                "Switch": {
                    "colorPrimary": "#000"
                },
                "Table": {
                    "colorPrimary": "#f04d3e",
                    "colorLink": "#000",
                    "colorLinkActive": "#000",
                    "colorLinkHover": "#43CDEB"
                },
                "Tabs": {
                    "colorPrimary": "#000"
                },
                "Transfer": {
                    "controlItemBgActive": "#f04d3e",
                    "controlItemBgActiveHover": "#43CDEB"
                },
                "Typography": {
                    "colorLink": "#000",
                    "colorLinkActive": "#000",
                    "colorLinkHover": "#f04d3e",
                    "fontWeightStrong": 500
                }
            }
        }, children: _jsx(DndProvider, { backend: HTML5Backend, children: _jsxs("div", { id: "app-inner", className: "full-height", children: [_jsx("div", { className: "preloaderbar hide", children: _jsx("span", { className: "bar" }) }), _jsx("div", { id: "app-main", className: classnames('full-height', {
                            'fixed-header': fixedHeader,
                            'boxed-layout': boxedLayout
                        }) + envClassName, children: _jsx(Elements, { stripe: StripeHelper.stripePromise, children: _jsx(Outlet, {}) }) }), _jsx(AuthenticationManager, {}), _jsx(GettingStartedIntroCarousel, {}), _jsx(NewVersionNotification, {})] }) }) }));
}
